import { useEffect, useState } from "react";
import {
  parseProgramColor,
  PROGRAM_COLOR_PICKER_FALLBACK,
} from "../shared/utils/programColor";

/**
 * Optional program color picker (native color input + hex code + Clear).
 * Empty means no staff color — chart uses the indigo fallback palette.
 */
export function ProgramColorSwatch({ color, title }) {
  const parsed = parseProgramColor(color);
  const hex = parsed.ok ? parsed.color : "";
  if (!hex) {
    return (
      <span
        className="program-color-swatch program-color-swatch--empty"
        title={title || "No color"}
        aria-label="No color"
      />
    );
  }
  return (
    <span
      className="program-color-swatch"
      style={{ backgroundColor: hex }}
      title={title || hex}
      aria-label={hex}
    />
  );
}

export default function ProgramColorInput({
  value,
  onChange,
  id,
  disabled,
  className = "",
  "aria-label": ariaLabel = "Program color",
}) {
  const parsed = parseProgramColor(value);
  const hex = parsed.ok ? parsed.color : "";
  const [hexText, setHexText] = useState(hex);
  const [hexInvalid, setHexInvalid] = useState(false);

  useEffect(() => {
    setHexText(hex);
    setHexInvalid(false);
  }, [hex]);

  function applyColor(nextHex) {
    setHexText(nextHex);
    setHexInvalid(false);
    onChange(nextHex);
  }

  return (
    <div className={`program-color-field ${className}`.trim()}>
      <input
        type="color"
        className="program-color-field__picker"
        value={hex || PROGRAM_COLOR_PICKER_FALLBACK}
        onChange={(e) => {
          const next = parseProgramColor(e.target.value);
          if (next.ok) applyColor(next.color);
        }}
        disabled={disabled}
        aria-label={`${ariaLabel} swatch`}
        title={hex || "Choose a color"}
      />
      <input
        id={id}
        type="text"
        className={`input program-color-field__hex${hexInvalid ? " program-color-field__hex--invalid" : ""}`}
        value={hexText}
        onChange={(e) => {
          const raw = e.target.value;
          setHexText(raw);
          const next = parseProgramColor(raw);
          if (next.ok) {
            setHexInvalid(false);
            onChange(next.color);
            if (next.color) setHexText(next.color);
          } else {
            setHexInvalid(true);
          }
        }}
        onBlur={() => {
          const next = parseProgramColor(hexText);
          if (next.ok) {
            applyColor(next.color);
            return;
          }
          setHexText(hex);
          setHexInvalid(false);
        }}
        disabled={disabled}
        placeholder="#fbbc04"
        maxLength={7}
        spellCheck={false}
        autoComplete="off"
        aria-label={`${ariaLabel} code`}
        aria-invalid={hexInvalid}
        title="Hex color, e.g. #fbbc04"
      />
      {hex ? (
        <button
          type="button"
          className="btn-link-muted"
          onClick={() => applyColor("")}
          disabled={disabled}
          title="Clear color"
        >
          Clear
        </button>
      ) : (
        <span className="program-color-field__none">None</span>
      )}
    </div>
  );
}
